import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

export function SyncManager() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { activeCompanyId } = useCompany();
  const [sellerSyncResult, setSellerSyncResult] = useState<string | null>(null);
  const [statSyncResult, setStatSyncResult] = useState<string | null>(null);

  const syncSellersMutation = useMutation({
    mutationFn: () =>
      apiPost<{ message: string }>(adminCompanyPath('/sync/sellers', activeCompanyId!)),
    onSuccess: (res) => {
      if (res.ok) {
        setSellerSyncResult(JSON.stringify(res.data, null, 2));
        queryClient.invalidateQueries({ queryKey: ['admin', 'sellers'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'api-config'] });
      } else {
        setSellerSyncResult(res.error.message);
      }
    },
    onError: (err) => {
      setSellerSyncResult((err as Error).message);
    },
  });

  const syncStatsMutation = useMutation({
    mutationFn: () =>
      apiPost<{ message: string }>(adminCompanyPath('/sync/stats', activeCompanyId!), {
        queryParams: { date: new Date().toISOString(), timeRange: 'monthly' },
      }),
    onSuccess: (res) => {
      if (res.ok) {
        setStatSyncResult(JSON.stringify(res.data, null, 2));
      } else {
        setStatSyncResult(res.error.message);
      }
    },
    onError: (err) => {
      setStatSyncResult((err as Error).message);
    },
  });

  if (!activeCompanyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.syncTitle')}</h1>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.syncTitle')}</h1>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>{t('admin.syncSellers')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.syncSellersDesc')}</p>
            <Button
              onClick={() => { setSellerSyncResult(null); syncSellersMutation.mutate(); }}
              disabled={syncSellersMutation.isPending}
            >
              {syncSellersMutation.isPending ? t('common.loading') : t('admin.syncNow')}
            </Button>
            {sellerSyncResult && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {sellerSyncResult}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>{t('admin.syncStats')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.syncStatsDesc')}</p>
            <Button
              onClick={() => { setStatSyncResult(null); syncStatsMutation.mutate(); }}
              disabled={syncStatsMutation.isPending}
            >
              {syncStatsMutation.isPending ? t('common.loading') : t('admin.syncNow')}
            </Button>
            {statSyncResult && (
              <pre className="mt-2 max-h-48 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-300">
                {statSyncResult}
              </pre>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
